import { Alert, AlertBody } from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Sparkle } from '@lifeomic/chromicons';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDocumentReference } from '../../../../hooks/useDocumentReference';
import {
  isSurveyFillAwaitingPersistCaption,
  isSurveyFillDisclaimerVisible,
  openDownloadedDocument,
  surveyFillCaptionKind,
  surveyFillLastFilledCaption,
  useSurveyFill,
  type SurveyFillFlowsheetSource,
  type SurveyFillInvokeErrorCode,
} from './useSurveyFill';

const messages = defineMessages({
  fill: {
    id: 'surveys.fillFromChart.button',
    defaultMessage: 'Fill from Chart',
  },
  filling: {
    id: 'surveys.fillFromChart.filling',
    defaultMessage: 'Filling from chart',
  },
  neverRun: {
    id: 'surveys.fillFromChart.neverRun',
    defaultMessage: 'Not filled from chart',
  },
  awaitingPersist: {
    id: 'surveys.fillFromChart.awaitingPersist',
    defaultMessage: 'Saving survey before Fill can start…',
  },
  lastFilled: {
    id: 'surveys.fillFromChart.lastFilled',
    defaultMessage: 'Last filled {when}',
  },
  lastFilledWithCount: {
    id: 'surveys.fillFromChart.lastFilledWithCount',
    defaultMessage:
      'Last filled {when} ({filledCount, plural, =0 {no answers pre-filled} one {# answer pre-filled} other {# answers pre-filled}}).',
  },
  lastFilledCountUnavailable: {
    id: 'surveys.fillFromChart.lastFilledCountUnavailable',
    defaultMessage: 'Last filled {when} (pre-filled count unavailable)',
  },
  lastFailed: {
    id: 'surveys.fillFromChart.lastFailed',
    defaultMessage: 'Last fill failed {when}. Try again.',
  },
  lastFailedWithReason: {
    id: 'surveys.fillFromChart.lastFailedWithReason',
    defaultMessage: 'Last fill failed {when}. {reason}',
  },
  fillingBanner: {
    id: 'surveys.fillFromChart.fillingBanner',
    defaultMessage:
      'Filling from chart. Answers will appear when this finishes. You can keep typing; Fill will not overwrite answers already on the form.',
  },
  invokeForbidden: {
    id: 'surveys.fillFromChart.invokeForbidden',
    defaultMessage: 'Fill is not allowed for this session.',
  },
  invokeNotFound: {
    id: 'surveys.fillFromChart.invokeNotFound',
    defaultMessage:
      'Fill service is not available yet. Try again in a few minutes.',
  },
  invokeConflict: {
    id: 'surveys.fillFromChart.invokeConflict',
    defaultMessage: 'A fill is already running for this survey.',
  },
  invokeNotReady: {
    id: 'surveys.fillFromChart.invokeNotReady',
    defaultMessage: 'Survey is still saving. Try Fill again in a moment.',
  },
  invokeUnknown: {
    id: 'surveys.fillFromChart.invokeUnknown',
    defaultMessage: 'Fill failed to start. Try again.',
  },
  statusUnavailable: {
    id: 'surveys.fillFromChart.statusUnavailable',
    defaultMessage:
      'Cannot check fill status. The fill service may not be available yet.',
  },
  aiDisclaimer: {
    id: 'surveys.fillFromChart.aiDisclaimer',
    defaultMessage:
      'Pre-filled answers may include AI suggestions and can be wrong or ' +
      'incomplete. Verify pre-filled answers before completing this form.',
  },
  comparedWith: {
    id: 'surveys.fillFromChart.comparedWithFlowsheet',
    defaultMessage: 'Compared with: PD Flowsheet filed {filedOn}',
  },
  comparedWithUndated: {
    id: 'surveys.fillFromChart.comparedWithFlowsheetUndated',
    defaultMessage: 'Compared with: PD Flowsheet',
  },
  viewFlowsheet: {
    id: 'surveys.fillFromChart.viewFlowsheet',
    defaultMessage: 'View',
  },
  viewFlowsheetFailed: {
    id: 'surveys.fillFromChart.viewFlowsheetFailed',
    defaultMessage: 'Could not open the PD Flowsheet. Try again.',
  },
});

const invokeErrorMessage = (
  code: SurveyFillInvokeErrorCode,
): keyof typeof messages => {
  if (code === 'forbidden') {
    return 'invokeForbidden';
  }
  if (code === 'not-found') {
    return 'invokeNotFound';
  }
  if (code === 'conflict') {
    return 'invokeConflict';
  }
  if (code === 'not-ready') {
    return 'invokeNotReady';
  }
  return 'invokeUnknown';
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  caption: {
    color: theme.palette.text.secondary,
  },
  compare: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  view: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    font: 'inherit',
    textDecoration: 'underline',
  },
}));

const relativeTime = (epochMs: number): string => {
  const deltaSec = Math.round((epochMs - Date.now()) / 1000);
  const abs = Math.abs(deltaSec);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (abs < 60) {
    return rtf.format(deltaSec, 'second');
  }
  if (abs < 3600) {
    return rtf.format(Math.round(deltaSec / 60), 'minute');
  }
  if (abs < 86400) {
    return rtf.format(Math.round(deltaSec / 3600), 'hour');
  }
  return rtf.format(Math.round(deltaSec / 86400), 'day');
};

export const testIds = {
  viewFlowsheet: 'survey-fill-flowsheet-view',
};

const FlowsheetCompareLine: React.FC<{
  source: SurveyFillFlowsheetSource;
}> = ({ source }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { retrieveDocumentReference, retrieveAttachment } =
    useDocumentReference();
  const [viewError, setViewError] = useState(false);
  const [opening, setOpening] = useState(false);

  const onView = () => {
    if (opening) {
      return;
    }
    setViewError(false);
    setOpening(true);
    void openDownloadedDocument(
      source.documentId,
      retrieveDocumentReference,
      async document => retrieveAttachment(document as fhir.DocumentReference),
      {
        openBlank: () => {
          const popup = window.open('about:blank', '_blank');
          if (popup) {
            popup.opener = null;
          }
          return popup;
        },
        openUrl: url => {
          window.open(url, '_blank', 'noopener,noreferrer');
        },
      },
    )
      .then(opened => {
        if (!opened) {
          setViewError(true);
        }
      })
      .catch(() => {
        setViewError(true);
      })
      .finally(() => {
        setOpening(false);
      });
  };

  return (
    <div>
      <div className={classes.compare}>
        <Text size="caption" className={classes.caption}>
          {source.filedOn
            ? formatMessage(messages.comparedWith, { filedOn: source.filedOn })
            : formatMessage(messages.comparedWithUndated)}
          {' ·'}
        </Text>
        <button
          type="button"
          className={classes.view}
          onClick={onView}
          disabled={opening}
          data-testid={testIds.viewFlowsheet}
        >
          {formatMessage(messages.viewFlowsheet)}
        </button>
      </div>
      {viewError ? (
        <Text size="caption" className={classes.caption}>
          {formatMessage(messages.viewFlowsheetFailed)}
        </Text>
      ) : null}
    </div>
  );
};

export const FillFromChartChrome: React.FC<{
  onFillingChange?: (isFilling: boolean) => void;
  onFilledResponse?: (response: fhir.QuestionnaireResponse) => void;
}> = ({ onFillingChange, onFilledResponse }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    showChrome,
    isInvokeReady,
    awaitingPersist,
    isInvokeQueued,
    lastSucceededAt,
    lastFailedAt,
    lastFailureReason,
    filledCount,
    flowsheetSource,
    invoke,
    isBusy,
    invokeError,
    statusError,
  } = useSurveyFill({ onFilledResponse });

  const fillRunning = isBusy;
  const showAwaitingPersistCaption = isSurveyFillAwaitingPersistCaption(
    awaitingPersist,
    isInvokeQueued,
  );

  useEffect(() => {
    onFillingChange?.(fillRunning);
  }, [fillRunning, onFillingChange]);

  if (!showChrome) {
    return null;
  }

  const captionKind = surveyFillCaptionKind({
    fillRunning,
    invokeError,
    statusError,
    awaitingPersist: showAwaitingPersistCaption,
    lastFailedAt,
    lastSucceededAt,
  });
  let caption = formatMessage(messages.neverRun);
  if (captionKind === 'filling') {
    caption = formatMessage(messages.filling);
  } else if (captionKind === 'invoke-error' && invokeError) {
    caption = formatMessage(messages[invokeErrorMessage(invokeError)]);
  } else if (captionKind === 'status-unavailable' && statusError) {
    caption =
      statusError === 'unknown'
        ? formatMessage(messages.statusUnavailable)
        : formatMessage(messages[invokeErrorMessage(statusError)]);
  } else if (captionKind === 'awaiting-persist') {
    caption = formatMessage(messages.awaitingPersist);
  } else if (captionKind === 'last-failed' && lastFailedAt) {
    caption = lastFailureReason
      ? formatMessage(messages.lastFailedWithReason, {
          when: relativeTime(lastFailedAt),
          reason: lastFailureReason,
        })
      : formatMessage(messages.lastFailed, {
          when: relativeTime(lastFailedAt),
        });
  } else if (captionKind === 'last-filled' && lastSucceededAt) {
    const lastFilled = surveyFillLastFilledCaption(
      relativeTime(lastSucceededAt),
      filledCount,
    );
    caption =
      lastFilled.kind === 'withCount'
        ? formatMessage(messages.lastFilledWithCount, {
            when: lastFilled.when,
            filledCount: lastFilled.filledCount,
          })
        : formatMessage(messages.lastFilledCountUnavailable, {
            when: lastFilled.when,
          });
  }
  const errorBanner =
    captionKind === 'invoke-error' || captionKind === 'status-unavailable'
      ? caption
      : undefined;
  const buttonDisabled = fillRunning || (!isInvokeReady && !awaitingPersist);

  return (
    <Box className={classes.root}>
      {fillRunning ? (
        <Alert statusType="info" fullWidth>
          <AlertBody>
            <Text size="caption">{formatMessage(messages.fillingBanner)}</Text>
          </AlertBody>
        </Alert>
      ) : errorBanner ? (
        <Alert statusType="error" fullWidth>
          <AlertBody>
            <Text size="caption">{errorBanner}</Text>
          </AlertBody>
        </Alert>
      ) : null}
      <div className={classes.row}>
        {fillRunning ? (
          <Tooltip title={formatMessage(messages.filling)}>
            <DotLoader size={0} />
          </Tooltip>
        ) : (
          <Tooltip title={formatMessage(messages.fill)}>
            <IconButton
              aria-label={formatMessage(messages.fill)}
              icon={Sparkle}
              onClick={invoke}
              disabled={buttonDisabled}
            />
          </Tooltip>
        )}
        <Text size="caption" className={classes.caption}>
          {caption}
        </Text>
      </div>
      {captionKind === 'last-filled' && flowsheetSource ? (
        <FlowsheetCompareLine
          key={flowsheetSource.documentId}
          source={flowsheetSource}
        />
      ) : null}
      {isSurveyFillDisclaimerVisible(lastSucceededAt) ? (
        <Alert statusType="warning" fullWidth>
          <AlertBody>
            <Text size="caption">{formatMessage(messages.aiDisclaimer)}</Text>
          </AlertBody>
        </Alert>
      ) : null}
    </Box>
  );
};
